import { fetchJson } from "./queryClient";

/**
 * The one query for "the projects this user can see".
 *
 * Until 2026-09-28 the same list was fetched under three keys on every load —
 * `["projects", all|assigned]` (App, Project Settings), `["/api/projects",
 * all|user]` (the bell, the picker) and `["/api/projects"]` (Dashboard) — so
 * each page paid for two or three identical requests, and a project row that
 * carried an inline cover image crossed the wire that many times.
 *
 * Invalidate with `{ queryKey: ["/api/projects"] }`: it is a prefix of this key.
 */
export function projectsListQuery(isSuperAdmin: boolean) {
  return {
    queryKey: ["/api/projects", isSuperAdmin ? "all" : "user"] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchJson<any[]>(isSuperAdmin ? "/api/projects?all=true" : "/api/projects", { signal }),
  };
}

/**
 * The one query for ONE project, `GET /api/projects/:id` — the page header,
 * the sidebar, Availability and the building-term hook all read this key.
 *
 * It must carry its own fetcher. The app's default fetcher requests
 * `queryKey[0]` alone, so a `useQuery({ queryKey: ["/api/projects", id] })`
 * with no `queryFn` fetched `/api/projects` — the whole LIST — and cached the
 * array under this project's key. Availability and useBuildingTerm did exactly
 * that, so on those pages the header read a name off an array and drew a "?"
 * with no title (Andrew, 2026-09-30), and `projectType` was always undefined,
 * so a tower was never recognised as one. tests/projectQueryKey.test.ts pins it.
 */
export function projectQuery(projectId: string | undefined) {
  return {
    queryKey: ["/api/projects", projectId] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchJson<any>(`/api/projects/${projectId}`, { signal }),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  };
}
